/**
 * MONSTER TOWN — PER-SYSTEM FRAME PROFILER. OWNED BY PERF (src/perf/**).
 *
 * `engine.stats.workMs` says the frame cost 38 ms. It does not say WHICH of the
 * twenty registered systems spent it, and guessing is how you spend a night
 * optimising the wrong one.
 *
 * This wraps every system's `fixed` and `render` for a measured window, totals
 * the time, and unwraps. It is a diagnostic, never on in a real frame: the
 * `performance.now()` pair around each call is itself measurable at this
 * granularity, so the numbers are for RANKING systems, not for gating.
 *
 * `src/core/engine.ts` is a frozen contract, so nothing is added to it — the
 * wrapping is done from outside, through the `engine.system(id)` accessor that
 * already exists.
 */

import { engine } from '../core/engine'

interface Slot {
  id: string
  fixedMs: number
  renderMs: number
  fixedCalls: number
  renderCalls: number
}

export interface SystemTiming {
  id: string
  /** Total ms in `fixed()` over the window. */
  fixedMs: number
  /** Total ms in `render()` over the window. */
  renderMs: number
  totalMs: number
  /** ms per rendered frame — the number to compare against the budget. */
  perFrameMs: number
}

type Fn = ((...a: never[]) => void) | undefined
interface Mutable {
  fixed?: (dt: number) => void
  render?: (alpha: number, frameMs: number) => void
}

let active = false

/**
 * Wrap every system, run for `ms`, unwrap, and report. Resolves after the
 * window; the caller must not place anything during it or the numbers include a
 * scene build.
 */
export async function profileSystems(ms: number): Promise<SystemTiming[]> {
  if (active) return []
  active = true

  const slots = new Map<string, Slot>()
  const originals: Array<{ sys: Mutable; fixed: Fn; render: Fn }> = []

  for (const id of engine.systemIds()) {
    const sys = engine.system(id) as unknown as Mutable | undefined
    if (sys === undefined) continue
    const slot: Slot = { id, fixedMs: 0, renderMs: 0, fixedCalls: 0, renderCalls: 0 }
    slots.set(id, slot)
    const originalFixed = sys.fixed
    const originalRender = sys.render
    originals.push({
      sys,
      fixed: originalFixed as Fn,
      render: originalRender as Fn,
    })
    if (originalFixed !== undefined) {
      sys.fixed = (dt: number): void => {
        const t = performance.now()
        originalFixed(dt)
        slot.fixedMs += performance.now() - t
        slot.fixedCalls++
      }
    }
    if (originalRender !== undefined) {
      sys.render = (alpha: number, frameMs: number): void => {
        const t = performance.now()
        originalRender(alpha, frameMs)
        slot.renderMs += performance.now() - t
        slot.renderCalls++
      }
    }
  }

  await new Promise<void>((resolve) => setTimeout(resolve, ms))

  for (const o of originals) {
    if (o.fixed === undefined) delete o.sys.fixed
    else o.sys.fixed = o.fixed as (dt: number) => void
    if (o.render === undefined) delete o.sys.render
    else o.sys.render = o.render as (alpha: number, frameMs: number) => void
  }
  active = false

  /*
   * ONE FRAME COUNT FOR EVERYBODY, NOT EACH SYSTEM'S OWN.
   *
   * `perFrameMs` used to divide a slot's total by ITS OWN `renderCalls`, which
   * is zero for every system that only registers `fixed` — and `Math.max(1, 0)`
   * then divided a five-second total by one frame. So a system costing 5 ms
   * across the whole window was ranked at "4.90 ms/frame", above systems that
   * really were spending a millisecond in every frame, and the printed TOTAL
   * (41 ms/frame against a measured 14.5) was arithmetic nonsense.
   *
   * That is exactly the wrong-system-to-optimise error this file's own header
   * warns about, so the divisor is now the number of frames the ENGINE drew —
   * the largest render-call count seen — and it is the same for every row.
   */
  let frames = 0
  for (const s of slots.values()) if (s.renderCalls > frames) frames = s.renderCalls
  frames = Math.max(1, frames)

  const out: SystemTiming[] = []
  for (const s of slots.values()) {
    out.push({
      id: s.id,
      fixedMs: s.fixedMs,
      renderMs: s.renderMs,
      totalMs: s.fixedMs + s.renderMs,
      perFrameMs: (s.fixedMs + s.renderMs) / frames,
    })
  }
  return out.sort((a, b) => b.totalMs - a.totalMs)
}
